import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { UserForm } from "../../components/users";
import { useCreateUser, useOnboarding } from "../../features/users/hooks";

/**
 * Page for creating a new user
 */
const CreateUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { createUser, isLoading } = useCreateUser();
  const { sendOnboardingEmail, isSending } = useOnboarding();
  const [error, setError] = useState<string | undefined>(undefined);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Handle form submission
  const handleSubmit = async (data: any) => {
    // Extract sendOnboardingEmail flag and prepare data for API
    const { sendOnboardingEmail: shouldSendEmail, ...userData } = data;

    // API will automatically use the organization from context
    const result = await createUser(userData);

    if (result.success) {
      // If sendOnboardingEmail is true, send the onboarding email
      if (shouldSendEmail && result.data?.id) {
        const emailResult = await sendOnboardingEmail(result.data.id);

        if (emailResult.success) {
          setNotification({
            open: true,
            message: "User created successfully and onboarding email sent.",
            severity: "success",
          });
        } else {
          setNotification({
            open: true,
            message: `User created successfully, but failed to send onboarding email: ${emailResult.error}`,
            severity: "warning",
          });
        }
      } else {
        setNotification({
          open: true,
          message:
            "User created successfully. You can send the onboarding email from the user list.",
          severity: "success",
        });
      }

      // Navigate back to users list after a short delay
      setTimeout(() => {
        navigate("/users");
      }, 2000);
    } else {
      setError(result.error);
    }
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li>
            <div className="flex items-center">
              <button
                onClick={() => navigate("/")}
                className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary"
              >
                Dashboard
              </button>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => navigate("/users")}
                className="ml-1 text-gray-700 hover:text-primary md:ml-2 dark:text-gray-300 dark:hover:text-primary"
              >
                Users
              </button>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-gray-500 md:ml-2 dark:text-gray-400">
                Create User
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)] mb-2">
        Create New User
      </h1>

      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Create a new user with role-based permissions
      </p>

      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <UserForm
          isEditMode={false}
          onSubmit={handleSubmit}
          isLoading={isLoading || isSending}
          error={error}
        />
      </div>

      {/* Notification */}
      {notification.open && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`
            max-w-xl w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden
            ${notification.severity === "success" ? "bg-green-50 dark:bg-green-900/20" : ""}
            ${notification.severity === "error" ? "bg-red-50 dark:bg-red-900/20" : ""}
            ${notification.severity === "warning" ? "bg-yellow-50 dark:bg-yellow-900/20" : ""}
            ${notification.severity === "info" ? "bg-accent-tint" : ""}
          `}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.severity === "success" && (
                    <svg
                      className="h-6 w-6 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                  {notification.severity === "error" && (
                    <svg
                      className="h-6 w-6 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1 pt-0.5">
                  <p
                    className={`
                    text-sm font-medium
                    ${notification.severity === "success" ? "text-green-800 dark:text-green-200" : ""}
                    ${notification.severity === "error" ? "text-red-800 dark:text-red-200" : ""}
                    ${notification.severity === "warning" ? "text-yellow-800 dark:text-yellow-200" : ""}
                    ${notification.severity === "info" ? "text-accent-strong" : ""}
                  `}
                  >
                    {notification.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    onClick={handleNotificationClose}
                    className={`
                      inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${notification.severity === "success" ? "text-green-500 hover:text-green-600 focus:ring-green-500" : ""}
                      ${notification.severity === "error" ? "text-red-500 hover:text-red-600 focus:ring-red-500" : ""}
                      ${notification.severity === "warning" ? "text-yellow-500 hover:text-yellow-600 focus:ring-yellow-500" : ""}
                      ${notification.severity === "info" ? "text-accent hover:text-accent-strong focus:ring-accent" : ""}
                    `}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUserPage;
